import React from 'react';
import { css } from '@emotion/react';
import { ButtonBase } from '../ButtonBase/ButtonBase';
import { TypographyBody, TypographyBodyProps } from '../Typography';
import { BulletIcon } from '../../icons/BulletIcon';
import type { TypographyBodySize, ColorValue } from '../../theming';
import { InfoItemsContext } from './InfoItemsContext';
import { startDirection } from '../../utils';
import { useHoneycombTheme } from '../../theming';

export type InfoItemTheme = {
    iconSize: number;
    iconClearance: number;
    fontSize: TypographyBodySize;
    bulletPaddingStart: number;
    bulletIconStart: number;
    iconColor: ColorValue;
};

const infoItemTheme: InfoItemTheme = {
    iconSize: 16,
    iconClearance: 8,
    fontSize: 's',
    bulletPaddingStart: 16,
    bulletIconStart: -6,
    iconColor: 'primary.base',
};
export interface InfoItemProps {
    /**
     * The text that will be displayed.
     */
    children: React.ReactNode;

    /**
     * If `true`, the component will be a button (using `ButtonBase`).
     * Use this if you want to apply an `onClick` handler.
     * @default false
     */
    button?: boolean;

    /**
     * The icon component.
     */
    icon?: React.ReactElement<any>;

    /**
     * Additional properties applied to the label component.
     */
    LabelProps?: TypographyBodyProps;

    /**
     * Permit additional properties which will be passed to the root node.
     */
    [x: string]: unknown;

    /**
     * Ref to be passed to each cloned child.
     */
    ref?: React.Ref<any>;
}

export function InfoItem(props: Readonly<InfoItemProps>): React.JSX.Element {
    const { button = false, children, icon, LabelProps, ref, ...other } = props;

    const { bullets } = React.useContext(InfoItemsContext);

    const color = button ? 'text.link' : undefined;

    const { theme } = useHoneycombTheme();

    const start = startDirection();

    const itemStyles = css`
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: ${infoItemTheme.iconClearance}px;
        ${button &&
        `
                z-index: ${theme.zIndex.foreground};
            `}
    `;
    // Note: The 'foreground' z-order ensures that the button is clickable over a background 'blocklink'. Applicable when used on a Card.

    const iconStyle = css`
        flex-shrink: 0;
        color: ${theme.fn.resolveColorValue(infoItemTheme.iconColor)};
        ${bullets &&
        `
                ${start}: ${infoItemTheme.bulletIconStart}px;
            `}
        ${(icon || bullets) &&
        `
                width: ${infoItemTheme.iconSize}px;
                height: ${infoItemTheme.iconSize}px;
            `}
    `;

    const itemContent = (
        <>
            {bullets && <BulletIcon css={iconStyle} />}
            {icon && React.cloneElement(icon, { css: iconStyle, 'data-info-item-icon': true })}
            <TypographyBody
                data-info-item-text={true}
                component="span"
                underlined={button}
                size={infoItemTheme.fontSize}
                color={color}
                {...LabelProps}
            >
                {children}
            </TypographyBody>
        </>
    );

    return button ? (
        <ButtonBase ref={ref} css={itemStyles} data-info-item={true} component="div" tabIndex={0} {...other}>
            {itemContent}
        </ButtonBase>
    ) : (
        <div ref={ref} css={itemStyles} data-info-item={true} {...other}>
            {itemContent}
        </div>
    );
}
